import React, { ReactNode } from "react";

import { defineMessage, useIntl } from "react-intl";

import ApiError from "@mapmycustomers/shared/util/api/ApiError";

import AuthLinkComponent from "../types/AuthLinkComponent";

const genericErrorMessage = defineMessage({
  id: "auth.login.error.unknown",
  defaultMessage: "Server issue, please try again later or contact support",
  description: "A generic login failed error message text",
});

// The messages below are matched on the server's English text.
const getServerMessage = (error: ApiError): string =>
  error.errors.flatMap(({ validationErrors }) => validationErrors ?? [])[0]?.message ??
  error.errors.find(({ message }) => message)?.message ??
  "";

export interface AuthErrorMessageOptions {
  forgotPasswordUrl: string;
  LinkComponent: AuthLinkComponent;
  onForgotPasswordClick?: () => void;
}

const useAuthErrorMessage = (
  error: ApiError | undefined,
  { forgotPasswordUrl, LinkComponent, onForgotPasswordClick }: AuthErrorMessageOptions
): ReactNode | undefined => {
  const intl = useIntl();
  if (!error) {
    return undefined;
  }

  if (
    (error.status === 401 || error.status === 403) &&
    error.message === "Password based login is disabled for your organization"
  ) {
    return intl.formatMessage({
      id: "auth.login.error.passwordLoginDisabled",
      defaultMessage: "Password based login is disabled for your organization",
      description: "Disabled password login error message text",
    });
  }

  if (error.status === 401) {
    if (!getServerMessage(error).startsWith("Your password has expired")) {
      // don't show an error when was redirected to the login page because of the expired token
      return undefined;
    }
    return intl.formatMessage(
      {
        id: "auth.login.error.passwordExpired",
        defaultMessage:
          "Your password has expired, please create a new password by clicking <link>Forgot password</link> below",
        description: "Expired password login error message text",
      },
      {
        link: (text: ReactNode) => (
          <LinkComponent onClick={onForgotPasswordClick} to={forgotPasswordUrl}>
            {text}
          </LinkComponent>
        ),
      }
    );
  }

  if (error.status === 429) {
    return intl.formatMessage({
      id: "auth.login.error.tooManyAttempts",
      defaultMessage: "Too many sign-in attempts, please try again in a few minutes",
      description: "Sign-in rate limit error message text",
    });
  }

  const message = getServerMessage(error);
  if (message.startsWith("Invalid grant")) {
    return intl.formatMessage({
      id: "auth.login.error.invalidCredentials",
      defaultMessage: "This username and password are incorrect, please try again",
      description: "An invalid credentials error message text",
    });
  }
  if (message.startsWith("This organization has been locked.")) {
    return intl.formatMessage({
      id: "auth.login.error.lockedOrganization",
      defaultMessage: "This organization has been locked. Contact support for more information.",
      description: "Locked organization error message text",
    });
  }
  if (message.startsWith("Your account has been deactivated.")) {
    return intl.formatMessage({
      id: "auth.login.error.accountDeactivated",
      defaultMessage: "Your account has been deactivated. Please contact your organization owner.",
      description: "Account Deactivated error message text",
    });
  }
  return intl.formatMessage(genericErrorMessage);
};

export default useAuthErrorMessage;
